import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Plus, Search, ShieldAlert, Trash2 } from 'lucide-react';
import InitialAvatar from '../../components/InitialAvatar';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';

type Provider = {
  id: string;
  type: string;
  userId?: string | null;
  firstName: string;
  lastName: string;
  title?: string | null;
  specialties: string[];
  languages: string[];
  telemedAvailable: boolean;
  allowOutsideWorkingSchedule: boolean;
  avatarUrl?: string | null;
  active: boolean;
  linkedUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  } | null;
};

function imagePreviewUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image/')) return trimmed;
  return '';
}

export default function SettingsProviders() {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { tr, t } = useI18n();

  const [providers, setProviders] = useState<Provider[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const providerData = await fetchJson<Provider[]>('/providers');
        if (active) setProviders(Array.isArray(providerData) ? providerData : []);
      } catch (err: any) {
        if (active) setFailure(err.message ?? tr('Failed to load providers'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchJson, tr]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return providers;
    return providers.filter((provider) => {
      const fullName = `${provider.firstName} ${provider.lastName}`.toLowerCase();
      return (
        fullName.includes(q) ||
        provider.type.toLowerCase().includes(q) ||
        (provider.title || '').toLowerCase().includes(q) ||
        provider.specialties.some((entry) => entry.toLowerCase().includes(q))
      );
    });
  }, [providers, search]);

  function openProfile(id: string) {
    navigate(`/settings/providers/${id}`);
  }

  async function handleDelete(id: string) {
    if (!window.confirm(tr('Delete this provider?'))) return;
    try {
      const res = await fetchApi(`/providers/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setProviders((prev) => prev.filter((provider) => provider.id !== id));
      setBanner(tr('Provider deleted'));
      setFailure(null);
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to delete provider'));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="rounded-full p-2 hover:bg-slate-100">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tr('Provider Directory')}</h1>
            <p className="text-sm text-slate-500">
              {tr('Open a provider profile to manage credentials, linked user access, and self-scheduling windows in one place.')}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/settings/providers/new')}
          className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white shadow-soft"
        >
          <Plus size={16} /> {tr('Add Provider')}
        </button>
      </div>

      {banner ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 size={16} /> {banner}
        </div>
      ) : null}
      {failure ? (
        <div className="flex items-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <ShieldAlert size={16} /> {failure}
        </div>
      ) : null}

      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 p-4">
          <Search size={16} className="text-slate-400" />
          <input
            className="w-full text-sm outline-none"
            placeholder={tr('Search providers...')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 text-center text-slate-500">
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" /> {t('common.loading', 'Loading...')}
              </span>
            </div>
          ) : null}
          {!loading && filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">{tr('No providers found.')}</div>
          ) : null}
          {!loading
            ? filtered.map((provider) => {
                const avatar = imagePreviewUrl(provider.avatarUrl || '');
                const linkedUserLabel = provider.linkedUser
                  ? `${provider.linkedUser.firstName} ${provider.linkedUser.lastName}`
                  : provider.userId
                    ? provider.userId
                    : tr('No linked user');
                return (
                  <div
                    key={provider.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`${tr('Open provider profile')}: ${provider.firstName} ${provider.lastName}`.trim()}
                    onClick={() => openProfile(provider.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openProfile(provider.id);
                      }
                    }}
                    className="group grid cursor-pointer gap-2 px-3 py-2.5 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-ink/10 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)_auto]"
                  >
                    <div className="flex items-start gap-2.5 text-left">
                      <InitialAvatar name={`${provider.firstName} ${provider.lastName}`} src={avatar} alt={`${provider.firstName} ${provider.lastName}`} className="h-8 w-8" />
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium text-ink">
                          {provider.lastName}, {provider.firstName}
                        </div>
                        <div className="text-xs text-slate-500">
                          {provider.title || provider.type}
                          {provider.title ? ` · ${provider.type}` : ''}
                        </div>
                        <div className="flex flex-wrap gap-1.5 pt-1 text-[10px]">
                          <span className={`rounded-full px-2 py-0.5 font-medium ${provider.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {provider.active ? tr('Active') : tr('Inactive')}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 font-medium ${provider.userId ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'}`}>
                            {provider.userId ? tr('Scheduling ready') : tr('Link user for scheduling')}
                          </span>
                          {provider.telemedAvailable ? (
                            <span className="rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">{tr('Telemed')}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1 text-xs text-slate-600">
                      <div>
                        <div className="font-semibold uppercase tracking-[0.16em] text-slate-400">{tr('Linked user')}</div>
                        <div className="mt-0.5">{linkedUserLabel}</div>
                      </div>
                      <div>
                        <div className="font-semibold uppercase tracking-[0.16em] text-slate-400">{tr('Specialties')}</div>
                        <div className="mt-0.5">{provider.specialties.length ? provider.specialties.join(', ') : tr('None')}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleDelete(provider.id);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        title={tr('Delete')}
                        aria-label={tr('Delete')}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      </div>
    </div>
  );
}
